import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Servicio que detecta si el backend FastAPI esta accesible.
 *
 * Casos que cubre:
 *  - VPN caida o no conectada (timeout al api-*.mascredimas.us)
 *  - VM CRE-PUNTOMAS-BACK01 deallocada por el StartStopVM v2 schedule
 *  - Container puntomas-api-{env} en restart loop
 *  - Network local roto
 *
 * En todos esos casos el browser ve un "fetch failed" / "TypeError: Failed
 * to fetch" que en el HttpInterceptor llega como status === 0. Sin este
 * servicio, ese error solo aparece cuando el usuario intenta una accion;
 * con ping al cargar, lo detectamos ANTES y avisamos.
 */
@Injectable({ providedIn: 'root' })
export class BackendHealthService {
  private readonly http = inject(HttpClient);
  private readonly snackBar = inject(MatSnackBar);

  private readonly _isUp$ = new BehaviorSubject<boolean>(true);
  /** True si el ultimo health check fue exitoso. Default true para no asustar antes de chequear. */
  readonly isUp$: Observable<boolean> = this._isUp$.asObservable();

  /**
   * Hace 1 ping a /api/health (timeout corto via retry-once schedule).
   * Si falla, muestra snackbar persistente y loggea explicito a consola.
   * Idempotente: se puede llamar varias veces sin acumular subscripciones.
   */
  checkOnce(): Observable<boolean> {
    const url = `${environment.apiUrl}/api/health`;

    return this.http.get<{ status: string }>(url).pipe(
      map(res => res?.status === 'healthy' || res?.status === 'ok' || !!res),
      catchError(err => {
        // status 0 = CORS preflight failure / network / DNS / VPN
        // 5xx = backend arrancado pero DB down u otro
        const isUnreachable = err?.status === 0;
        console.error(
          `[BackendHealth] /api/health FAILED (status=${err?.status}). URL=${url}`,
          isUnreachable
            ? '— probable causa: VPN desconectada, VM apagada o container en restart loop'
            : '— el backend respondio pero con error',
          err
        );
        return of(false);
      })
    ).pipe(
      take(1),
      switchMap(ok => {
        this._isUp$.next(ok);
        if (!ok) this.notifyDown();
        return of(ok);
      })
    );
  }

  private notifyDown(): void {
    this.snackBar.open(
      'Sin conexion con el servidor. Verifica VPN/red o contacta soporte.',
      'Cerrar',
      { duration: 10000, panelClass: ['snack-backend-down'] }
    );
  }

  /** Marca el backend como down y dispara el snackbar — llamado por el error interceptor. */
  reportDown(): void {
    if (this._isUp$.value) {
      this._isUp$.next(false);
      this.notifyDown();
    }
  }

  /** Re-ping para confirmar que volvio. Util tras un error transitorio. */
  reportPossibleRecovery(): void {
    // Esquivar tormenta: esperar 2s antes de re-chequear.
    timer(2000).pipe(switchMap(() => this.checkOnce())).subscribe();
  }
}
